import {
    Component,
    OnInit,
    EventEmitter,
    Output,
    AfterViewInit,
    ChangeDetectorRef,
} from '@angular/core';
import { PolicyRepoService } from '@client/app/services/api/policy-repo/policy-repo.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { LabelRepo } from '@monsido/modules/endpoints/api/label.repo';
import { PolicyHelperService } from '@client/app/blocks/helpers/policy-helper/policy.helper.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { PolicyService } from '@client/app/services/entities/policy/policy.service';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { GlobalPolicyDialogsTargetsComponent } from '@client/app/pages/global/policy/dialogs/targets/targets.component';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { asyncExecute } from '@client/app/core/utils/async-execute';
import { PolicyModel } from '@client/app/services/api/policy-repo/policy-model';
import { EntityWithDropdownType } from '@client/types/entity-with-dropdown.type';
import { LabelsEntity } from '@monsido/modules/models/api/interfaces/domain.interface';
import { MonTableCollection } from '@client/ng2/models/table-collection.interface';

@Component({
    selector: 'global-policy-matches',
    standalone: false,
    template: `
        <table-container [collection]="filteredPolicies"
                         noResultMessage="{{'No policies found' | translate}}"
                         [disablePagination]="true"
                         [progress]="progress">
            <table class="table table-hover table-fixed table-with-labels">
                <thead>
                <tr>
                    <th class="col-3"></th>
                    <th translate>Title</th>
                    <!--<th class="col-8" translate>Modules</th>--> <!-- Will be added later on when When we can split policy compliance for seo, accessibility and QA -->
                    <th class="col-9 text-center" colspan="3" translate>Hits across all domains and modules</th>
                    <!--<th class="col-8" translate>Total compliance</th>--> <!-- Can't implement this right now -->
                    <th class="col-5"></th>
                </tr>
                </thead>
                <tbody>
                <tr *ngFor="let policy of filteredPolicies; let i = index"
                    (mouseover)="showLabel[policy.id!] = true"
                    (mouseleave)="showLabel[policy.id!] = false">
                    <td class="text-v-middle text-center" [attr.aria-label]="'Status' | translate">
                        <mon-table-policy-type
                                *ngIf="policy.state !== 'running'"
                                class="inline-block"
                                [passed]="policy.matches_count === 0"
                                [tooltip]="getTooltipMessage(policy)"
                                [target]="getPolicyTarget(policy)">
                        </mon-table-policy-type>
                        <span *ngIf="policy.state === 'running'"
                              [attr.aria-label]="'Running' | translate"
                              [ngClass]="['GLOBAL', 'POLICIES', 'RUNNING'] | monIcons"
                              class="fa-2x fa-spin"></span>
                    </td>
                    <td class="text-v-middle">
                        <policy-information [policy]="policy" class="inline-block">
                        </policy-information>
                        <label-table *ngIf="showLabels"
                                     [assignedLabels]="policy.labels"
                                     (getLabels)="getLabels()"
                                     [existingLabels]="allLabels"
                                     (onUpdateLabel)="updatePolicy(policy)"
                                     (click)="$event.stopPropagation()">
                        </label-table>
                    </td>
                    <!--<td class="text-v-middle">--><!-- Will be added later on when When we can split policy compliance for seo, accessibility and QA -->
                        <!--<span class="text-grey inline-block" ng-class="{'inner-l-mini': $index > 0}" ng-repeat="tag in policy.tags">-->
                            <!--<span uib-tooltip="{{ getTooltipForTag(tag) }}" class="{{ ['POLICIES', 'TAGS', tag.toUpperCase()] | monIcons }}"></span>-->
                        <!--</span>-->
                    <!--</td>-->
                    <td></td>
                    @let matchesPercentage = { value: 0 };
                    <td class="text-center text-v-middle cursor-pointer"
                        (click)="openTargets(policy)"
                        mon-set-background-color
                        (percentage)="matchesPercentage.value = $event"
                        color="#4384F3"
                        [count]="policy.matches_count"
                        [maxCount]="maxCount">
                        <span [ngClass]="{'text-white': matchesPercentage.value > 0.4}">{{ policy.matches_count | monNumber }}</span>
                    </td>
                    <td *ngIf="policy.state !== 'running'"></td>
                    <td class="text-right text-v-middle"
                        [attr.aria-label]="'Actions' | translate"
                        *ngIf="policy.state !== 'running'">
                        <mon-action-menu [monActionMenuItems]="policy.dropdown!">
                            <span translate>Action</span>
                        </mon-action-menu>
                    </td>
                    <td class="text-v-middle text-secondary-4 text-right"
                        *ngIf="policy.state === 'running'"
                        colspan="2" translate>
                        Policy scan in progress
                    </td>
                </tr>
                </tbody>
            </table>
        </table-container>

        <ng-template #tooltipTemplate let-policy="policy">
            {{ getTooltipMessage(policy) }}
        </ng-template>
    `,
})
export class GlobalPolicyMatchesComponent implements OnInit, AfterViewInit {
    @Output() openPolicyForm = new EventEmitter();
    @Output() getPolicies = new EventEmitter();

    filteredPolicies: MonTableCollection<EntityWithDropdownType<PolicyModel>> = [];
    maxCount: number = 0;
    allLabels: LabelsEntity[] = [];
    showLabels: boolean = false;
    showLabel: Record<number, boolean> = {};
    progress: Promise<MonTableCollection<PolicyModel>> | null = null;

    constructor (
        private policyRepoService: PolicyRepoService,
        private activeFeatureService: ActiveFeatureService,
        private labelRepo: LabelRepo,
        private policyHelperService: PolicyHelperService,
        private monEventService: MonEventService,
        private policyService: PolicyService,
        private monModalService: MonModalService,
        private translateService: TranslateService,
        private cdRef: ChangeDetectorRef,
    ) {}

    ngAfterViewInit () {
        this.cdRef.detectChanges();
    }

    trackByIndex (index: number, item: any): any {
        return item.id;
    }

    ngOnInit (): void {
        this.allLabels = [];
        this.showLabels = this.activeFeatureService.isFeatureActive('labels');
        this.getLabels();
        this.getTopMatchesPolicies().then();
    }

    private updateDropdowns (policies: EntityWithDropdownType<PolicyModel>[]): EntityWithDropdownType<PolicyModel>[] {
        for (const policy of policies) {
            const dropdown = [
                {
                    label: this.translateService.getString('Run policy again'),
                    leftIcon: 'faPlay',
                    action: () => this.runPolicy(policy),
                    eventParams: { action: 'policy-run', params: { name: policy.name } },
                    shouldShow: policy.state !== 'running' && policy.state !== 'queued',
                },
                {
                    label: this.translateService.getString('Policy in queue'),
                    leftIcon: 'faPlay',
                    disabled: true,
                    shouldShow: policy.state === 'queued',
                },
                {
                    label: this.translateService.getString('Edit'),
                    leftIcon: 'faEdit',
                    action: () => this.openPolicyForm.emit({ policy: policy }),
                    eventParams: { action: 'before-policy-edit', params: { name: policy.name } },
                },
                {
                    label: this.translateService.getString('Archive'),
                    leftIcon: 'faArchive',
                    action: () => this.archivePolicy(policy),
                    eventParams: { action: 'policy-archive', params: { name: policy.name, archiveStatus: policy.archived } },
                },
                {
                    label: this.translateService.getString('Delete'),
                    leftIcon: 'faTrashAlt',
                    action: () => this.deletePolicy(policy),
                    eventParams: { action: 'policy-delete', params: { name: policy.name } },
                },
            ];

            policy.dropdown = dropdown.filter((option: any) => option.shouldShow !== false).map((item: any) => {
                return {
                    label: item.label,
                    leftIcon: item.leftIcon,
                    disabled: item.disabled,
                    action: () => {
                        if (item.eventParams) {
                            this.monEventService.run('actionTrigger', item.eventParams);
                        }

                        if (item.action) {
                            item.action();
                        }
                    },
                };
            });
        }

        return policies;
    }

    private deletePolicy (policy: any): void {
        this.monModalService
            .confirm(
                this.translateService.getString('Are you sure you want to delete the policy <strong>[[policyName]]</strong>', {
                    policyName: policy.name,
                }),
            )
            .then(() => {
                this.policyService.destroy(policy.id).then(() => {
                    this.getPolicies.emit();
                }, () => {});
            }, () => {});
    }

    private async runPolicy (policy: any): Promise<void> {
        await asyncExecute(this.monModalService
            .confirm(
                this.translateService.getString('Are you sure you want to run the policy <strong>[[policyName]]</strong> again?', {
                    policyName: policy.name,
                }),
            ));
        await asyncExecute(this.policyService.run(policy.id));
        await asyncExecute(this.getTopMatchesPolicies());
        this.startAutoReload();
    }

    openTargets (policy: any): void {
        const params = {
            body: GlobalPolicyDialogsTargetsComponent,
            size: 'md',
            data: { policy: policy },
        };
        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, { params });
    }

    getPolicyTarget (policy: any): any {
        return this.policyHelperService.getType(policy);
    }

    getTooltipMessage (policy: any): any {
        return this.policyHelperService.getTooltipMessageForType(policy);
    }

    private async getTopMatchesPolicies (): Promise<any> {
        const params: any = {
            page_size: 4,
            sort_by: 'matches_count',
            sort_dir: 'desc',
        };
        this.progress = this.policyRepoService.getAll(params);
        const [getAllPoliciesError, policies] = await asyncExecute(this.progress);
        if (getAllPoliciesError) {
            return;
        }

        this.filteredPolicies = this.updateDropdowns(
            policies.sort((a: any, b: any) => {
                return a.matches_count > b.matches_count ? -1 : a.matches_count < b.matches_count ? 1 : 0;
            }),
        );

        this.maxCount = this.filteredPolicies?.[0].matches_count || 0;
    }

    getLabels (): void {
        this.labelRepo.getLabels().then((data: any) => {
            this.allLabels = data;
        }, () => {});
    }

    updatePolicy (policy: any): void {
        const cPolicy: any = {};
        cPolicy.id = policy.id;
        cPolicy.labels = policy.labels.map((label: any) => {
            return label.id;
        });
        this.policyUpdate(cPolicy).then(() => {}, () => {});
    }

    private async archivePolicy (policy: any): Promise<void> {
        await asyncExecute(this.monModalService
            .confirm(
                this.translateService.getString(
                    'Are you sure you want to archive the policy <strong>[[policyName]]</strong>? We will no longer look for matches to this policy. You can always unarchive the policy again later on.',
                    {
                        policyName: policy.name,
                    },
                ),
            ));
        const clone = {
            id: policy.id,
            archived: !policy.archived,
        };
        await asyncExecute(this.policyUpdate(clone));
        this.getPolicies.emit();
    }

    private startAutoReload (): void {
        setTimeout(async () => {
            await asyncExecute(this.getTopMatchesPolicies());
            const runningPolicies = this.filteredPolicies.filter((policy: any) => {
                return policy.state === 'running';
            }).length;

            if (runningPolicies > 0) {
                this.startAutoReload();
            }
        }, 30000);
    }

    private getTooltipForTag (tag: any): any {
        return this.policyHelperService.getTag(tag);
    }

    private policyUpdate (policy: any): Promise<any> {
        return this.policyService.update(policy);
    }
}
